import axios from "axios";

/**
 * Fetch the full price breakdown for an order or quotation from the backend.
 *
 * Returns stored snapshot values when lock_order_prices is true,
 * otherwise recalculates live and persists the result.
 *
 * OPTIMIZATION NOTE (Phase 2):
 * - TableRow.jsx no longer uses this function. It now displays prices directly from
 *   the overview endpoint response (total_price, subtotal, vat_total, delivery_price).
 * - This function is still used by OrderPopup.jsx for detailed price breakdowns.
 * - Consider consolidating price data endpoints in the future to reduce API calls.
 *
 * @param {number} labelId
 * @param {number} orderId
 * @returns {Promise<{
 *   subtotal: number,
 *   mandatory_costs: Array<{name: string, price: number}>,
 *   company_discount: number,
 *   discount_codes_amount: number,
 *   minimum_order_surcharge: number,
 *   shipping: number,
 *   vat: number,
 *   total: number,
 *   is_snapshot: boolean,
 * }>}
 */
export const fetchOrderPriceBreakdown = async (labelId, orderId) => {
    const response = await axios.get(`/api/${labelId}/orders/${orderId}/price-breakdown`);
    return response.data;
};

const toNumeric = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const toModelPayload = (model) => ({
    id: model?.id ?? null,
    file: model?.file ?? model?.title ?? null,
    quantity: Number.parseInt(model?.amount ?? model?.pivot?.amount ?? 1, 10),
    unit_price: model?.price ?? model?.pivot?.price ?? 0,
    discount_multiplier: toNumeric(model?.discount_multiplier ?? model?.pivot?.discount_multiplier, 1),
    admin_edited: Boolean(model?.admin_edited),
    discount_price:
        model?.pivot?.discount_price !== undefined && model?.pivot?.discount_price !== null
            ? toNumeric(model.pivot.discount_price)
            : null,
    settings: model?.settings ?? {},
    mandatory_options: model?.mandatory_options ?? model?.mandatoryOptions ?? [],
});

const toLibraryProductPayload = (product) => ({
    id: product?.id ?? null,
    title: product?.title ?? product?.file ?? null,
    quantity: Number.parseInt(product?.quantity ?? 1, 10),
    unit_price: toNumeric(product?.price),
    discount_multiplier: toNumeric(product?.discount_multiplier, 1),
    settings_id: product?.settings_id ?? product?.settings?.settingsId ?? null,
    settings: product?.settings ?? null,
});

/**
 * Build canonical request payload for POST /api/{labelId}/pricing/cart-breakdown.
 */
export const buildCartBreakdownPayload = ({
    models = [],
    libraryProducts = [],
    discountCodes = [],
    customerContext = {},
    deliveryContext = {},
    orderContext = null,
    userExclusions = [],
}) => {
    const normalizedDiscountCodes = discountCodes
        .map((code) => (typeof code === "string" ? code : code?.code))
        .filter(Boolean);

    return {
        cart: {
            models: models.map(toModelPayload),
            library_products: libraryProducts.map(toLibraryProductPayload),
            discount_codes: normalizedDiscountCodes,
        },
        customer_context: {
            company_id: customerContext?.company_id ?? null,
            user_id: customerContext?.user_id ?? null,
            vat_number: customerContext?.vat_number ?? null,
            country: customerContext?.country ?? null,
        },
        delivery_context: {
            country: deliveryContext?.country ?? null,
            postal_code: deliveryContext?.postal_code ?? null,
            shipping_cost: toNumeric(deliveryContext?.shipping_cost, 0),
            show_shipping_costs: Boolean(deliveryContext?.show_shipping_costs),
        },
        order_context: orderContext
            ? {
                  order_id: orderContext?.order_id ?? orderContext?.id ?? null,
                  status: orderContext?.status ?? null,
                  lock_order_prices: Boolean(orderContext?.lock_order_prices),
              }
            : null,
        user_exclusions: userExclusions,
    };
};

/**
 * Normalize canonical + backward-compatible pricing response into one structure.
 */
export const normalizeCartBreakdownResponse = (responseData = {}) => {
    const totals = responseData?.totals ?? {
        subtotal: toNumeric(responseData?.subtotal),
        company_discount: toNumeric(responseData?.company_discount),
        discount_codes_amount: toNumeric(responseData?.discount_codes_amount),
        minimum_order_surcharge: toNumeric(responseData?.minimum_order_surcharge),
        shipping: toNumeric(responseData?.shipping),
        vat: toNumeric(responseData?.vat),
        total: toNumeric(responseData?.total),
    };

    const lines = responseData?.lines ?? {
        models: [],
        library_products: [],
        mandatory_costs: responseData?.mandatory_costs ?? [],
    };

    const metadata = responseData?.metadata ?? {
        is_snapshot: Boolean(responseData?.is_snapshot),
        lock_order_prices: false,
        currency: "EUR",
        schema_version: "cart-pricing.v1",
        calculated_at: null,
        label_id: null,
        order_id: null,
    };

    return {
        ...responseData,
        totals,
        lines,
        metadata,

        // Keep top-level aliases for current consumers
        subtotal: toNumeric(totals.subtotal),
        mandatory_costs: lines.mandatory_costs ?? [],
        company_discount: toNumeric(totals.company_discount),
        discount_codes_amount: toNumeric(totals.discount_codes_amount),
        minimum_order_surcharge: toNumeric(totals.minimum_order_surcharge),
        shipping: toNumeric(totals.shipping),
        vat: toNumeric(totals.vat),
        total: toNumeric(totals.total),
        is_snapshot: Boolean(metadata.is_snapshot),
    };
};

/**
 * Fetch live checkout cart breakdown from backend pricing service.
 */
export const fetchCartPriceBreakdown = async (labelId, payload) => {
    const response = await axios.post(`/api/${labelId}/pricing/cart-breakdown`, payload);
    return normalizeCartBreakdownResponse(response.data);
};
